/**
 * Push preference storage, shared by the map SPA (web-push.js) and the
 * /volg settings panel (volg-settings.js).
 *
 * localStorage only — this is the device's mirror of what the backend holds.
 * Persisting a change to the server differs per caller (the SPA re-registers
 * with its coordinates; /volg posts a preference-only update that must not
 * touch the stored location), so that stays with the callers.
 */

import { STORAGE_KEYS, NOTIFICATION_RADIUS_KM_DEFAULT } from './config.js';
import { getDistanceKm } from './utils.js';

export function getPushRadius() {
    const stored = localStorage.getItem(STORAGE_KEYS.webPushRadius);
    return stored ? parseFloat(stored) : NOTIFICATION_RADIUS_KM_DEFAULT;
}

export function setPushRadius(km) {
    localStorage.setItem(STORAGE_KEYS.webPushRadius, String(km));
}

/** Returns null when no filter is set — null means "all services". */
export function getPushServices() {
    const stored = localStorage.getItem(STORAGE_KEYS.webPushServices);
    if (!stored) return null;
    try {
        const arr = JSON.parse(stored);
        return Array.isArray(arr) && arr.length > 0 ? arr : null;
    } catch { return null; }
}

export function setPushServices(services) {
    if (!services || services.length === 0) {
        localStorage.removeItem(STORAGE_KEYS.webPushServices);
    } else {
        localStorage.setItem(STORAGE_KEYS.webPushServices, JSON.stringify(services));
    }
}

// --- Per-kind preferences (backend: services/push.py) ---
//
// One JSON object under webPushPrefs, in the backend's own field names so it
// goes into a request body as-is. Defaults match the backend's column
// defaults, which every device was moved onto.

export const PREF_DEFAULTS = {
    alert_level: 'off',          // off | zwaailicht | all — meldingen in de buurt
    burst_scope: 'nearby',       // all | nearby | off — pieken
    city_alerts: false,          // gevolgde steden: ook meldingen met zwaailicht
    trending: false,             // veelbekeken
    quiet_start: null,           // Amsterdam hours; null = geen stille uren
    quiet_end: null,
};

export function getPushPrefs() {
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem(STORAGE_KEYS.webPushPrefs) || 'null'); } catch { /* defaults */ }
    return { ...PREF_DEFAULTS, ...(stored && typeof stored === 'object' ? stored : {}) };
}

export function setPushPrefs(changes) {
    const prefs = { ...getPushPrefs(), ...changes };
    localStorage.setItem(STORAGE_KEYS.webPushPrefs, JSON.stringify(prefs));
    return prefs;
}

/** Whether any setting needs this device's location on the server. */
export function usesLocation(prefs = getPushPrefs()) {
    return prefs.alert_level !== 'off' || prefs.burst_scope === 'nearby';
}

/** The preference part of a /api/push/register or /settings body. */
export function prefsBody() {
    return { radius_km: getPushRadius(), services: getPushServices(), ...getPushPrefs() };
}

// Moves smaller than this don't change which meldingen fall in a radius of
// a few km, so they aren't worth a request.
const LOCATION_RESEND_KM = 0.5;

/**
 * Keep the location the backend matches "in de buurt" against current.
 * It was stored once, at registration, so on a phone "near me" meant near
 * wherever push was switched on. Called whenever the map learns a location,
 * and by the settings panel's "Deel je locatie"; sends only when this
 * device's settings use one and it moved. True when the backend now has it.
 */
export async function updatePushLocation(lat, lon) {
    const token = localStorage.getItem(STORAGE_KEYS.webPushToken);
    if (!token || !usesLocation()) return false;
    try {
        const last = JSON.parse(localStorage.getItem(STORAGE_KEYS.pushLocationSent) || 'null');
        if (last && getDistanceKm(last.lat, last.lon, lat, lon) < LOCATION_RESEND_KM) return true;
    } catch { /* resend */ }
    try {
        const resp = await fetch('/api/push/location', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, lat, lon }),
        });
        // 400 = outside the Netherlands; nothing to retry either way.
        if (resp.ok) localStorage.setItem(STORAGE_KEYS.pushLocationSent, JSON.stringify({ lat, lon }));
        return resp.ok;
    } catch (err) {
        console.error('[WebPush] Location update failed:', err);
        return false;
    }
}
